import { RootState } from "../../../redux-store";
import { CargoEvent, WeightAddedCargoEvent } from "./types";
import Decimal from "decimal.js-light";
import { DateTime, Duration } from "luxon";
import { createSelector } from "@reduxjs/toolkit";
import { chunk } from "lodash";

const EMPTY: CargoEvent[] = [];

export const selectCargoEvents = (cargoId: string | null) =>
  (state: RootState) =>
    cargoId ? (state.data.cargoEvents.entities[cargoId] ?? EMPTY) : EMPTY;

export const selectCargoDriverTimeTrackingTotalTime = (
  cargoId: string | null
) =>
  createSelector(selectCargoEvents(cargoId), (cargoEvents) => {
    const types = [
      "DRIVER_CARGO_TIME_TRACKING_STARTED",
      "DRIVER_CARGO_TIME_TRACKING_PAUSED",
      "DRIVER_CARGO_TIME_TRACKING_RESUMED",
      "DRIVER_CARGO_TIME_TRACKING_STOPPED",
    ];
    const timeTrackingCargoEvents = cargoEvents.filter((event) =>
      types.includes(event.type)
    );
    const hasStart = timeTrackingCargoEvents.find(
      (e) => e.type === "DRIVER_CARGO_TIME_TRACKING_STARTED"
    );
    if (!hasStart) {
      return null;
    }
    const hasStop = timeTrackingCargoEvents.find(
      (e) => e.type === "DRIVER_CARGO_TIME_TRACKING_STOPPED"
    );
    if (!hasStop) {
      return null;
    }

    let totalTimeInMs = 0;
    for (let i = 0; i < timeTrackingCargoEvents.length - 1; i++) {
      const currentEvent = timeTrackingCargoEvents[i];
      const nextEvent = timeTrackingCargoEvents[i + 1];
      if (currentEvent.type === "DRIVER_CARGO_TIME_TRACKING_PAUSED") {
        continue;
      }
      totalTimeInMs += DateTime.fromISO(nextEvent.time).diff(
        DateTime.fromISO(currentEvent.time)
      ).milliseconds;
    }
    return durationToHoursAndMinutes(Duration.fromMillis(totalTimeInMs));
  });

export const selectWeightCargoEvents = (cargoId: string | null) =>
  createSelector(
    selectCargoEvents(cargoId),
    (events) =>
      events.filter(
        (event) => event.type === "CARGO_WEIGHT_ADDED"
      ) as WeightAddedCargoEvent[]
  );

export const selectTotalWeight = (cargoId: string | null) =>
  createSelector(selectWeightCargoEvents(cargoId), (events) =>
    events.reduce((acc, event) => acc.plus(event.weight), new Decimal(0))
  );

export const selectPauseTime = (cargoId: string | null) =>
  createSelector(selectCargoEvents(cargoId), (events) => {
    const types = [
      "DRIVER_CARGO_TIME_TRACKING_PAUSED",
      "DRIVER_CARGO_TIME_TRACKING_RESUMED",
    ];
    let timeTrackingCargoEvents = events.filter((event) =>
      types.includes(event.type)
    );
    if (!timeTrackingCargoEvents.length) {
      return null;
    }
    if (
      timeTrackingCargoEvents[timeTrackingCargoEvents.length - 1].type ===
      "DRIVER_CARGO_TIME_TRACKING_PAUSED"
    ) {
      timeTrackingCargoEvents = timeTrackingCargoEvents.slice(0, -1);
    }
    if (timeTrackingCargoEvents.length % 2 !== 0) {
      throw new Error("Invalid time tracking events for cargo");
    }

    if (!timeTrackingCargoEvents.length) {
      return null;
    }

    let totalTimeInMs = 0;
    const pairs = chunk(timeTrackingCargoEvents, 2);
    for (const pair of pairs) {
      totalTimeInMs += DateTime.fromISO(pair[1].time).diff(
        DateTime.fromISO(pair[0].time)
      ).milliseconds;
    }

    return durationToHoursAndMinutes(Duration.fromMillis(totalTimeInMs));
  });

export const selectTimeSpent = (cargoId: string | null) =>
  createSelector(selectCargoEvents(cargoId), (events) => {
    const timeRelevantEvents = events?.filter((e) =>
      [
        "CARGO_COLLECTED",
        "CARGO_DELIVERED",
        "DRIVER_ARRIVED",
        "DRIVER_DEPARTED",
      ].includes(e.type)
    );
    if (timeRelevantEvents?.length > 1) {
      timeRelevantEvents.sort((a, b) => {
        return new Date(a.time).getTime() - new Date(b.time).getTime();
      });
      const firstEvent = timeRelevantEvents[0];
      const lastEvent = timeRelevantEvents[timeRelevantEvents.length - 1];
      const lastEventTime = DateTime.fromISO(lastEvent.time).set({
        second: 0,
      });
      const firstEventTime = DateTime.fromISO(firstEvent.time).set({
        second: 0,
        millisecond: 0,
      });
      return durationToHoursAndMinutes(lastEventTime.diff(firstEventTime));
    }
    return null;
  });

const durationToHoursAndMinutes = (duration: Duration) => {
  const hours = Math.floor(duration.as("hours"));
  const minutes = Math.floor(duration.as("minutes")) % 60;
  return { hours, minutes };
};
